/**
 * Campaign Banner - Complete JavaScript
 * Professional Shopify Implementation
 * 
 * Features:
 * - Copy discount code to clipboard
 * - Live countdown timer
 * - Close banner functionality
 * - localStorage persistence
 * - Analytics tracking
 */

(function() {
  'use strict';
  
  // Prevent double initialization
  if (window.CampaignBanner && window.CampaignBanner._initialized) {
    console.warn('[Campaign Banner] Already initialized, skipping...');
    return;
  }
  
  // Configuration
  var CONFIG = {
    COPY_SUCCESS_DURATION: 2000,
    COUNTDOWN_INTERVAL: 1000,
    URGENCY_THRESHOLD: 86400,
    STORAGE_PREFIX: 'campaign_banner_closed_'
  };
  
  // State
  var countdownIntervals = [];
  
  /**
   * Initialize when DOM is ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  function init() {
    initCopyButtons();
    initCountdowns();
    initCloseButtons();
    checkClosedBanners();
  }
  
  /* ==========================================
     COPY CODE TO CLIPBOARD
     ========================================== */
  
  function initCopyButtons() {
    var copyButtons = document.querySelectorAll('[data-copy-code]');
    
    copyButtons.forEach(function(button) {
      // Check if already initialized to prevent double-binding
      if (button.dataset.campaignBannerInit) return;
      button.dataset.campaignBannerInit = 'true';
      
      button.addEventListener('click', handleCopyClick);
    });
  }
  
  function handleCopyClick(event) {
    var button = event.currentTarget;
    var code = button.getAttribute('data-copy-code');
    var productId = button.getAttribute('data-product-id');
    
    // Copy to clipboard
    copyToClipboard(code)
      .then(function() {
        showCopySuccess(button);
        trackCopyEvent(code, productId);
      })
      .catch(function(err) {
        console.error('[Campaign Banner] Failed to copy code:', err);
        // Fallback: show code in alert
        alert('Cod reducere: ' + code);
      });
  }
  
  function copyToClipboard(text) {
    // Modern Clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(text);
    }
    
    // Fallback for older browsers
    return new Promise(function(resolve, reject) {
      var textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '0';
      textarea.style.opacity = '0';
      textarea.setAttribute('readonly', '');
      
      document.body.appendChild(textarea);
      
      try {
        textarea.select();
        textarea.setSelectionRange(0, 99999);
        
        var successful = document.execCommand('copy');
        document.body.removeChild(textarea);
        
        if (successful) {
          resolve();
        } else {
          reject(new Error('Copy command failed'));
        }
      } catch (err) {
        document.body.removeChild(textarea);
        reject(err);
      }
    });
  }
  
  function showCopySuccess(button) {
    var textSpan = button.querySelector('.copy-btn__text');
    var successSpan = button.querySelector('.copy-btn__success');
    
    if (!textSpan || !successSpan) return;
    
    // Show success message
    textSpan.style.display = 'none';
    successSpan.style.display = 'inline';
    
    // Disable button temporarily
    button.disabled = true;
    
    // Reset after duration
    setTimeout(function() {
      textSpan.style.display = 'inline';
      successSpan.style.display = 'none';
      button.disabled = false;
    }, CONFIG.COPY_SUCCESS_DURATION);
  }
  
  function trackCopyEvent(code, productId) {
    // Google Analytics 4 - safe check
    if (typeof window.gtag !== 'undefined') {
      try {
        window.gtag('event', 'copy_discount_code', {
          'event_category': 'Campaign',
          'event_label': code,
          'value': productId
        });
      } catch (e) {
        console.warn('[Campaign Banner] GA4 tracking failed:', e);
      }
    }
    
    // Facebook Pixel - safe check
    if (typeof window.fbq !== 'undefined') {
      try {
        window.fbq('trackCustom', 'CopyDiscountCode', {
          code: code,
          product_id: productId
        });
      } catch (e) {
        console.warn('[Campaign Banner] FB Pixel tracking failed:', e);
      }
    }
    
    // Console log for debugging
    console.log('[Campaign Banner] Code copied:', code);
  }
  
  /* ==========================================
     COUNTDOWN TIMER
     ========================================== */
  
  function initCountdowns() {
    var countdowns = document.querySelectorAll('[data-countdown]');
    
    countdowns.forEach(function(countdown) {
      // Check if already initialized
      if (countdown.dataset.campaignBannerInit) return;
      countdown.dataset.campaignBannerInit = 'true';
      
      var endTimestamp = parseInt(countdown.getAttribute('data-countdown'), 10);
      
      if (!endTimestamp || isNaN(endTimestamp)) {
        console.warn('[Campaign Banner] Invalid countdown timestamp');
        return;
      }
      
      // Initial update
      updateCountdown(countdown, endTimestamp);
      
      // Start interval
      var intervalId = setInterval(function() {
        var shouldContinue = updateCountdown(countdown, endTimestamp);
        
        if (!shouldContinue) {
          clearInterval(intervalId);
        }
      }, CONFIG.COUNTDOWN_INTERVAL);
      
      // Store interval ID for cleanup
      countdownIntervals.push(intervalId);
    });
  }
  
  function updateCountdown(element, endTimestamp) {
    var now = Math.floor(Date.now() / 1000);
    var remaining = endTimestamp - now;
    
    // Campaign ended
    if (remaining <= 0) {
      element.innerHTML = '<span style="color: #fbbf24; font-weight: 700;">Campanie încheiată</span>';
      return false; // Stop updating
    }
    
    // Calculate time components
    var days = Math.floor(remaining / 86400);
    var hours = Math.floor((remaining % 86400) / 3600);
    var minutes = Math.floor((remaining % 3600) / 60);
    
    // Update DOM
    var daysSpan = element.querySelector('.countdown__days');
    var hoursSpan = element.querySelector('.countdown__hours');
    var minutesSpan = element.querySelector('.countdown__minutes');
    
    if (daysSpan) daysSpan.textContent = days + 'z';
    if (hoursSpan) hoursSpan.textContent = hours + 'o';
    if (minutesSpan) minutesSpan.textContent = minutes + 'm';
    
    // Add urgency styling if less than 24 hours
    if (remaining < CONFIG.URGENCY_THRESHOLD) {
      var banner = element.closest('[data-campaign-banner]');
      if (banner) {
        banner.classList.add('campaign-banner--urgent');
      }
    }
    
    return true; // Continue updating
  }
  
  /* ==========================================
     CLOSE BUTTON
     ========================================== */
  
  function initCloseButtons() {
    var closeButtons = document.querySelectorAll('[data-close-banner]');
    
    closeButtons.forEach(function(button) {
      // Check if already initialized
      if (button.dataset.campaignBannerInit) return;
      button.dataset.campaignBannerInit = 'true';
      
      button.addEventListener('click', handleCloseClick);
    });
  }
  
  function handleCloseClick(event) {
    var button = event.currentTarget;
    var banner = button.closest('[data-campaign-banner]');
    
    if (!banner) return;
    
    // Get product ID for storage
    var productId = button.getAttribute('data-product-id');
    
    // Animate out
    banner.style.animation = 'fadeOut 0.3s ease-out';
    
    setTimeout(function() {
      banner.classList.add('campaign-banner--hidden');
      
      // Save preference
      if (productId) {
        saveClosedState(productId);
      }
      
      // Track close event
      trackCloseEvent(productId);
    }, 300);
  }
  
  function saveClosedState(productId) {
    try {
      var key = CONFIG.STORAGE_PREFIX + productId;
      var value = Date.now().toString();
      localStorage.setItem(key, value);
    } catch (e) {
      console.warn('[Campaign Banner] localStorage not available:', e);
    }
  }
  
  function checkClosedBanners() {
    var banners = document.querySelectorAll('[data-campaign-banner]');
    
    banners.forEach(function(banner) {
      var closeButton = banner.querySelector('[data-close-banner]');
      if (!closeButton) return;
      
      var productId = closeButton.getAttribute('data-product-id');
      if (!productId) return;
      
      // Check if user previously closed this banner
      try {
        var key = CONFIG.STORAGE_PREFIX + productId;
        var closedTime = localStorage.getItem(key);
        
        if (closedTime) {
          // Hide banner (user closed it before)
          banner.classList.add('campaign-banner--hidden');
        }
      } catch (e) {
        // localStorage not available, do nothing
      }
    });
  }
  
  function trackCloseEvent(productId) {
    if (typeof window.gtag !== 'undefined') {
      try {
        window.gtag('event', 'close_campaign_banner', {
          'event_category': 'Campaign',
          'value': productId
        });
      } catch (e) {
        console.warn('[Campaign Banner] GA4 tracking failed:', e);
      }
    }
    
    console.log('[Campaign Banner] Banner closed for product:', productId);
  }
  
  /* ==========================================
     CLEANUP
     ========================================== */
  
  // Clean up intervals when page unloads
  window.addEventListener('beforeunload', function() {
    countdownIntervals.forEach(function(intervalId) {
      clearInterval(intervalId);
    });
  });
  
  // Public API (optional, for external access)
  window.CampaignBanner = {
    /**
     * Initialization flag
     */
    _initialized: true,
    
    /**
     * Reset closed state for a product
     * Usage: CampaignBanner.resetClosedState(productId)
     */
    resetClosedState: function(productId) {
      try {
        var key = CONFIG.STORAGE_PREFIX + productId;
        localStorage.removeItem(key);
        
        // Reload to show banner again
        window.location.reload();
      } catch (e) {
        console.error('[Campaign Banner] Failed to reset banner state:', e);
      }
    },
    
    /**
     * Clear all closed states
     * Usage: CampaignBanner.clearAllClosedStates()
     */
    clearAllClosedStates: function() {
      try {
        Object.keys(localStorage).forEach(function(key) {
          if (key.startsWith(CONFIG.STORAGE_PREFIX)) {
            localStorage.removeItem(key);
          }
        });
        
        window.location.reload();
      } catch (e) {
        console.error('[Campaign Banner] Failed to clear banner states:', e);
      }
    },
    
    /**
     * Get version
     */
    version: '1.0.0'
  };
  
  // Log initialization
  console.log('[Campaign Banner] Initialized v' + window.CampaignBanner.version);
  
})();